import { useEffect, useState } from "react";
import { X, Plus, Minus, ShoppingBag, Heart } from "lucide-react";
import { useCart } from "../context/CartContext";

export const ProductModal = ({ product, onClose }) => {
    const { addItem } = useCart();
    const [size, setSize] = useState(product?.sizes?.[0]);
    const [qty, setQty] = useState(1);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    if (!product) return null;

    const handleAdd = () => {
        addItem({
            product_id: product.id,
            product_name: product.name,
            size: size.label,
            unit_price: size.price,
            quantity: qty,
            line_total: size.price * qty,
        });
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6"
            style={{ background: "rgba(59, 13, 33, 0.5)" }}
            onClick={onClose}
            data-testid="product-modal-overlay"
        >
            <div
                className="bg-white rounded-t-3xl md:rounded-3xl w-full max-w-2xl overflow-hidden sss-shadow-lg fade-up"
                onClick={(e) => e.stopPropagation()}
                data-testid="product-modal"
            >
                <div className="grid md:grid-cols-2">
                    <div className="aspect-square md:aspect-auto bg-[var(--sss-blush)] relative">
                        <img
                            src={product.image}
                            alt={product.name}
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    </div>
                    <div className="p-6 md:p-8 relative">
                        <button
                            type="button"
                            onClick={onClose}
                            className="absolute right-4 top-4 p-2 rounded-full hover:bg-[var(--sss-blush)]"
                            aria-label="Close"
                            data-testid="product-modal-close"
                        >
                            <X size={20} />
                        </button>
                        <h3 className="font-serif-display text-2xl md:text-3xl font-bold pr-8" data-testid="product-modal-title">
                            {product.name} <span className="ml-1">{product.emoji}</span>
                        </h3>
                        <p className="mt-3 text-sm text-[var(--sss-text-muted)] leading-relaxed">
                            {product.description}
                        </p>

                        <div className="mt-6">
                            <p className="text-xs uppercase tracking-widest text-[var(--sss-text-muted)] mb-2">
                                Choose a size
                            </p>
                            <div className="flex flex-col gap-2">
                                {product.sizes.map((s) => (
                                    <button
                                        key={s.label}
                                        type="button"
                                        onClick={() => setSize(s)}
                                        className={`flex items-center justify-between px-4 py-3 rounded-2xl border text-left transition ${
                                            size.label === s.label
                                                ? "border-[var(--sss-pink)] bg-[var(--sss-blush)]"
                                                : "border-[var(--sss-border)] hover:border-[var(--sss-pink)]"
                                        }`}
                                        data-testid={`product-size-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        <span className="text-sm font-medium">{s.label}</span>
                                        <span className="font-serif-display font-semibold">${s.price.toFixed(2)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="mt-6 flex items-center justify-between">
                            <div className="flex items-center gap-2 border border-[var(--sss-border)] rounded-full p-1">
                                <button
                                    type="button"
                                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                                    className="p-2 rounded-full hover:bg-[var(--sss-blush)]"
                                    aria-label="Decrease quantity"
                                    data-testid="product-qty-decrease"
                                >
                                    <Minus size={16} />
                                </button>
                                <span className="w-8 text-center font-semibold" data-testid="product-qty-value">{qty}</span>
                                <button
                                    type="button"
                                    onClick={() => setQty((q) => q + 1)}
                                    className="p-2 rounded-full hover:bg-[var(--sss-blush)]"
                                    aria-label="Increase quantity"
                                    data-testid="product-qty-increase"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                            <div className="font-serif-display text-xl font-bold" data-testid="product-modal-total">
                                ${(size.price * qty).toFixed(2)}
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handleAdd}
                            className="btn-pink w-full justify-center mt-6"
                            data-testid="product-add-to-cart"
                        >
                            <ShoppingBag size={16} /> Add to Cart
                            <Heart size={14} fill="#fff" strokeWidth={0} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
